import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import type { DeviceName, DeviceWarning } from './deviceWarning'
import { ReactComponent as MicOffIcon } from 'assets/icons/MicrophoneOff.svg'
import { ReactComponent as CameraOffIcon } from 'assets/icons/VideoOff.svg'

import styles from './devicePermission.module.scss'

export type BlockedDevice = {
  device: DeviceName
  warning: DeviceWarning
}

export type DevicePermissionDialogProps = BlockedDevice & {
  onClose: () => void
}

export function DevicePermissionDialog({ device, warning, onClose }: DevicePermissionDialogProps) {
  const { t } = useTranslation()

  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const Icon = device === 'microphone' ? MicOffIcon : CameraOffIcon

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-permission-message"
        onClick={(event) => event.stopPropagation()}
      >
        <span className={styles.icon} aria-hidden>
          <Icon width={28} height={28} />
        </span>

        <p className={styles.message} id="device-permission-message">
          {t(warning.messageKey)}
        </p>

        <button ref={closeRef} type="button" className={styles.action} onClick={onClose}>
          {t('chat.call.devicePermission.close')}
        </button>
      </div>
    </div>,
    document.body
  )
}
